import React, { useEffect } from 'react';
import { filterNullish } from '@modernhealth/filter-nullish';
import {
  BodyTextSmall,
  HeadingText50,
  SectionHeaderSkeleton,
} from '@modernhealth/vital-components';
import { captureException } from '@sentry/browser';

import { useGetMonthsData } from 'src/shared/hooks/useGetMonthsData';
import { isPublicTopic } from 'src/shared/utils/isPublicCircle';

import { MonthSection } from './MonthSection';
import { SessionCardRowSkeleton } from './SessionCardSkeletons';
import { FetchSessionsByDateTimeQuery } from '../documents/sessions.generated';

type Month = {
  monthStart: string;
  monthEnd: string;
  upcomingYear: boolean;
};

type Data = {
  key: Month;
  data: {
    circleSessionsByDateTime: FetchSessionsByDateTimeQuery['circleSessionsByDateTime'] | null;
  };
}[];

export const UpcomingSessionsZeroState = () => {
  return (
    <div css={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <HeadingText50>No upcoming sessions</HeadingText50>
      <BodyTextSmall>There are no scheduled sessions in the near future.</BodyTextSmall>
    </div>
  );
};

const DisplayUpcomingByTime: React.FC<{ currentMoment: string }> = ({ currentMoment }) => {
  const numberOfMonthsToDisplay = 3;
  const byTimeData = useGetMonthsData(numberOfMonthsToDisplay, currentMoment);

  const data = byTimeData.data as Data;
  const monthsData = data?.map(
    (month: {
      key: Month;
      data: {
        circleSessionsByDateTime: FetchSessionsByDateTimeQuery['circleSessionsByDateTime'] | null;
      };
    }) => {
      const anyEnglishSessions = month.data.circleSessionsByDateTime?.edges.some(
        edge => edge && isPublicTopic(edge.node.topic),
      );

      return {
        connection: month?.data
          .circleSessionsByDateTime as FetchSessionsByDateTimeQuery['circleSessionsByDateTime'],
        monthStartDate: month.key.monthStart,
        upcomingYear: month.key.upcomingYear,
        anyEnglishSessions,
      };
    },
  );

  useEffect(() => {
    if (!monthsData || byTimeData.hasError) {
      captureException(new Error('Circles failed to load'));
    }
  }, [monthsData, byTimeData.hasError]);
  if (byTimeData.loading) {
    return (
      <div>
        <SectionHeaderSkeleton />
        <SessionCardRowSkeleton />

        <SectionHeaderSkeleton />
        <SessionCardRowSkeleton />

        <SectionHeaderSkeleton />
        <SessionCardRowSkeleton />
      </div>
    );
  }

  const sessionCount = monthsData?.reduce((a, b) => a + b.connection?.totalCount, 0);
  const anyEnglishLessons = monthsData.some(data => data.anyEnglishSessions);

  // If non of the sessions available are in english, we should show the empty state
  if (!sessionCount || !anyEnglishLessons) return <UpcomingSessionsZeroState />;
  return (
    <>
      {monthsData?.map((month, i) => {
        const sessions = filterNullish(
          month.connection?.edges.map(edge => edge?.node) ?? [],
        ).filter(node => isPublicTopic(node.topic));

        return (
          <MonthSection
            key={`${month.monthStartDate}-${i}`}
            monthStartDate={month.monthStartDate}
            upcomingYear={month.upcomingYear}
            sessions={sessions}
          />
        );
      })}
    </>
  );
};

export default DisplayUpcomingByTime;
